import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Alert, Badge } from 'react-bootstrap';
import './ArtikelListe.css';
import { Link } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaQrcode, FaShoppingCart, FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';

const ArtikelListe = () => {
  const [artikel, setArtikel] = useState([]);
  const [filteredArtikel, setFilteredArtikel] = useState([]);
  const [suchbegriff, setSuchbegriff] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // Artikel vom Backend laden
  const loadArtikel = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/artikel');
      setArtikel(response.data);
      setFilteredArtikel(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Artikel:', err);
      setError('Die Artikel konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Beim ersten Rendern Artikel laden
  useEffect(() => {
    loadArtikel();
  }, []);

  // Artikel filtern basierend auf Suchbegriff und sortieren nach Name
  useEffect(() => {
    // Basis-Sortierung nach Namen
    const sortedArtikel = [...artikel].sort((a, b) => a.name.localeCompare(b.name));
    
    if (suchbegriff.trim() === '') {
      setFilteredArtikel(sortedArtikel);
    } else {
      const lowerSuchbegriff = suchbegriff.toLowerCase();
      const filtered = sortedArtikel.filter(item => 
        item.name.toLowerCase().includes(lowerSuchbegriff) ||
        (item.artikelnummer && item.artikelnummer.toLowerCase().includes(lowerSuchbegriff)) ||
        (item.kategorie && item.kategorie.toLowerCase().includes(lowerSuchbegriff)) ||
        (item.lagerort && item.lagerort.toLowerCase().includes(lowerSuchbegriff))
      );
      setFilteredArtikel(filtered);
    }
  }, [suchbegriff, artikel]);

  // Artikel löschen
  const handleDelete = async (id, name) => {
    if (window.confirm(`Möchten Sie den Artikel "${name}" wirklich löschen?`)) {
      try {
        setDeleteError(null);
        await axios.delete(`/api/artikel/${id}`);
        // Artikelliste neu laden
        loadArtikel();
      } catch (err) {
        console.error('Fehler beim Löschen des Artikels:', err);
        setDeleteError(`Der Artikel "${name}" konnte nicht gelöscht werden. Er wird möglicherweise noch in einer Bestellung verwendet.`);
      }
    }
  };

  // Artikel zum Warenkorb hinzufügen
  const addToCart = async (artikelId) => {
    try {
      await axios.post('/api/warenkorb', { artikelId, menge: 1 });
      alert('Artikel wurde zum Warenkorb hinzugefügt.');
    } catch (err) {
      console.error('Fehler beim Hinzufügen zum Warenkorb:', err);
      alert('Der Artikel konnte nicht zum Warenkorb hinzugefügt werden.');
    }
  };

  return (
    <Container>
      <h1 className="mb-4">Artikelverwaltung</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {deleteError && <Alert variant="danger">{deleteError}</Alert>}
      
      <Row className="mb-4">
        <Col md={6} className="mb-3 mb-md-0">
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Artikel suchen..."
              value={suchbegriff}
              onChange={(e) => setSuchbegriff(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={6} className="d-flex justify-content-md-end">
          <Button 
            as="a" 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              alert('Bitte verwenden Sie Ihre externe QR-Scanner-App, um Artikel zu scannen.');
            }}
            variant="outline-secondary"
            className="me-2"
            aria-label="QR-Code Info"
          >
            <FaQrcode /> <span className="d-none d-md-inline">QR-Code-Info</span>
          </Button>
          <Button 
            as={Link} 
            to="/artikel/neu" 
            variant="primary"
            aria-label="Neuen Artikel hinzufügen"
          >
            <FaPlus /> <span className="d-none d-md-inline">Neuer Artikel</span>
          </Button>
        </Col>
      </Row>
      
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
          <p className="mt-2">Artikel werden geladen...</p>
        </div>
      ) : filteredArtikel.length === 0 ? (
        <Card className="text-center p-4 shadow-sm">
          <Card.Body>
            <h5>Keine Artikel gefunden</h5>
            <p className="text-muted">
              {suchbegriff ? 
                `Es wurden keine Artikel gefunden, die "${suchbegriff}" enthalten.` : 
                'Es sind noch keine Artikel im System erfasst.'}
            </p>
            <Button 
              as={Link} 
              to="/artikel/neu" 
              variant="primary"
            >
              <FaPlus className="me-2" /> Ersten Artikel anlegen
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div>
          {/* Kategorien-Navigation */}
          <div className="bg-light p-2 mb-4 rounded shadow-sm sticky-top">
            <div className="d-flex flex-wrap gap-2">
              {(() => {
                // Extrahiere alle vorhandenen Kategorien
                const kategorien = [...new Set(filteredArtikel.map(a => a.kategorie || 'Ohne Kategorie'))].sort();
                return kategorien.map(kategorie => (
                  <a 
                    key={kategorie} 
                    href={`#kategorie-${kategorie.replace(/\s+/g, '-').toLowerCase()}`}
                    className="btn btn-sm btn-outline-primary"
                  >
                    {kategorie}
                  </a>
                ));
              })()}
            </div>
          </div>
          
          {/* Nach Kategorien gruppierte Artikel */}
          {(() => {
            // Artikel nach Kategorien gruppieren
            const kategorien = {};
            filteredArtikel.forEach(artikel => {
              const kategorie = artikel.kategorie || 'Ohne Kategorie';
              if (!kategorien[kategorie]) {
                kategorien[kategorie] = [];
              }
              kategorien[kategorie].push(artikel);
            });
            
            // Sortierte Kategorien-Keys erstellen (alphabetisch)
            const sortedKategorien = Object.keys(kategorien).sort();
            
            return sortedKategorien.map(kategorie => (
              <Card className="shadow-sm mb-4" key={kategorie} id={`kategorie-${kategorie.replace(/\s+/g, '-').toLowerCase()}`}>
                <Card.Header className="bg-light">
                  <h5 className="mb-0">{kategorie}</h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0 table-fixed-width">
                      <thead className="bg-light">
                        <tr>
                          <th className="col-fixed-width">Artikel</th>
                          <th className="col-fixed-width">Lieferant</th>
                          <th className="col-fixed-width">Lagerbestand</th>
                          <th className="col-fixed-width">Preis</th>
                          <th className="col-fixed-width">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kategorien[kategorie].map(artikel => (
                          <tr key={artikel.id}>
                            <td className="text-truncate cell-fixed-width">
                              <Link to={`/artikel/${artikel.id}`} className="text-decoration-none text-truncate">
                                {artikel.name}
                                {artikel.artikelnummer && <small className="text-muted ms-1">({artikel.artikelnummer})</small>}
                              </Link>
                              {artikel.lagerbestand === 0 && (
                                <Badge bg="danger" text="white" className="ms-2" title="Nicht auf Lager">
                                  <FaExclamationTriangle size={10} /> Nicht vorrätig
                                </Badge>
                              )}
                              {artikel.lagerbestand > 0 && artikel.lagerbestand < 5 && (
                                <Badge bg="warning" text="dark" className="ms-2" title="Niedriger Bestand">
                                  <FaExclamationTriangle size={10} /> Wenige
                                </Badge>
                              )}
                            </td>
                            <td className="text-truncate cell-fixed-width">{artikel.lieferant || '-'}</td>
                            <td className="text-truncate cell-fixed-width">
                              {artikel.lagerbestand} {artikel.einheit || 'Stk.'}
                            </td>
                            <td className="text-truncate cell-fixed-width">
                              {artikel.preis ? `${artikel.preis.toFixed(2)} €` : '-'}
                            </td>
                            <td className="text-center cell-fixed-width">
                              <Button 
                                variant="outline-success" 
                                size="sm" 
                                className="me-1 mb-1 mb-md-0" 
                                title="Zum Warenkorb hinzufügen"
                                onClick={() => addToCart(artikel.id)}
                              >
                                <FaShoppingCart />
                              </Button>
                              <Button 
                                as={Link} 
                                to={`/artikel/${artikel.id}/bearbeiten`} 
                                variant="outline-primary" 
                                size="sm" 
                                className="me-1 mb-1 mb-md-0" 
                                title="Artikel bearbeiten"
                              >
                                <FaEdit />
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm" 
                                className="mb-1 mb-md-0" 
                                title="Artikel löschen"
                                onClick={() => handleDelete(artikel.id, artikel.name)}
                              >
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            ));
          })()}
          
          <div className="mt-3">
            <small className="text-muted">
              {filteredArtikel.length} {filteredArtikel.length === 1 ? 'Artikel' : 'Artikel'} gefunden
            </small>
          </div>
        </div>
      )}
    </Container>
  );
};

export default ArtikelListe;
